import React from 'react';
import { Phone, FileText, CheckCircle, MapPin, AlertTriangle } from 'lucide-react';
export function DotNumberPage() {
  const requiredStates = ['Alabama', 'Alaska', 'Arizona', 'Colorado', 'Florida', 'Georgia', 'Iowa', 'Kansas', 'Kentucky', 'Maine', 'Michigan', 'Minnesota', 'Missouri', 'New York', 'Ohio', 'Oklahoma', 'Oregon', 'South Dakota', 'Tennessee', 'Utah', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'];
  return <div className="min-h-screen bg-gray-50" data-id="element-5480">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white py-16" data-id="element-5481">
        <div className="container mx-auto px-4" data-id="element-5482">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center" data-id="element-5483">
            What is a USDOT Number?
          </h1>
          <p className="text-xl text-gray-100 text-center" data-id="element-5484">
            Understanding Federal Motor Carrier Registration Requirements
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12" data-id="element-5485">
        <div className="max-w-4xl mx-auto" data-id="element-5486">
          {/* What is a USDOT Number */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-5487">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-5488">
              <FileText className="mr-3 text-[#B1050B]" size={28} data-id="element-5489" />
              USDOT Number Overview
            </h2>

            <div className="space-y-6" data-id="element-5490">
              <p className="text-lg text-gray-700" data-id="element-5491">
                Companies that operate commercial vehicles transporting
                passengers or hauling cargo in interstate commerce must be
                registered with the FMCSA and must have a USDOT Number.
              </p>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-6" data-id="element-5492">
                <p className="text-gray-800 mb-3" data-id="element-5493">
                  <strong data-id="element-5494">Also required for:</strong>
                </p>
                <p className="text-gray-700" data-id="element-5495">
                  Commercial intrastate hazardous materials carriers who haul
                  quantities requiring a safety permit must register for a USDOT
                  Number.
                </p>
              </div>

              <div className="bg-green-50 rounded-lg p-6" data-id="element-5496">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-5497">
                  Purpose of USDOT Number
                </h3>
                <p className="text-gray-700" data-id="element-5498">
                  The USDOT Number serves as a unique identifier when collecting
                  and monitoring a company's safety information acquired during:
                </p>
                <ul className="mt-3 space-y-2 text-gray-700" data-id="element-5499">
                  <li className="flex items-start" data-id="element-5500">
                    <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-5501" />
                    <span data-id="element-5502">Audits</span>
                  </li>
                  <li className="flex items-start" data-id="element-5503">
                    <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-5504" />
                    <span data-id="element-5505">Compliance reviews</span>
                  </li>
                  <li className="flex items-start" data-id="element-5506">
                    <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-5507" />
                    <span data-id="element-5508">Crash investigations</span>
                  </li>
                  <li className="flex items-start" data-id="element-5509">
                    <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-5510" />
                    <span data-id="element-5511">Inspections</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* New-Entrant Program */}
          <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white rounded-lg shadow-xl p-8 mb-8" data-id="element-5512">
            <h2 className="text-3xl font-bold mb-6" data-id="element-5513">New-Entrant Program</h2>

            <div className="space-y-4" data-id="element-5514">
              <div className="bg-white/10 rounded-lg p-6" data-id="element-5515">
                <p className="text-lg mb-4" data-id="element-5516">
                  All first-time carrier applicants for a USDOT Number will be
                  automatically enrolled in the{' '}
                  <strong data-id="element-5517">FMCSA New Entrant Safety Assurance Program</strong>.
                </p>

                <div className="space-y-3" data-id="element-5518">
                  <div className="flex items-start" data-id="element-5519">
                    <AlertTriangle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-5520" />
                    <div data-id="element-5521">
                      <p className="font-semibold mb-1" data-id="element-5522">
                        Program Requirements:
                      </p>
                      <p className="text-gray-100" data-id="element-5523">
                        New entrants must pass a safety audit and maintain
                        acceptable roadside safety performance over an initial
                        18-month period before they are given permanent
                        registration status.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 text-gray-900 rounded-lg p-6" data-id="element-5524">
                <h3 className="font-bold mb-2" data-id="element-5525">Exemptions</h3>
                <p data-id="element-5526">
                  In most cases, companies operating exclusively as brokers or
                  non-vehicle-operating shippers or freight forwarders do not
                  need to obtain a USDOT Number.
                </p>
              </div>
            </div>
          </div>

          {/* State Requirements */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-5527">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-5528">
              <MapPin className="mr-3 text-green-600" size={28} data-id="element-5529" />
              State-Specific Requirements
            </h2>

            <div className="bg-green-50 border-2 border-green-500 rounded-lg p-6 mb-6" data-id="element-5530">
              <p className="text-lg text-gray-800 mb-4" data-id="element-5531">
                <strong data-id="element-5532">
                  In select states, all registrants of commercial motor
                  vehicles, even intrastate and non-Motor Carrier registrants,
                  are required to obtain a USDOT Number as a necessary condition
                  for commercial vehicle registration.
                </strong>
              </p>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-5533">
              States Requiring USDOT Numbers (24 States):
            </h3>

            <div className="grid md:grid-cols-3 gap-3" data-id="element-5534">
              {requiredStates.map(state => <div key={state} className="bg-green-100 rounded-lg p-3 flex items-center" data-id="element-5535">
                  <CheckCircle className="text-green-600 mr-2 flex-shrink-0" size={18} data-id="element-5536" />
                  <span className="text-gray-800 font-medium" data-id="element-5537">{state}</span>
                </div>)}
            </div>
          </div>

          {/* Who Needs a USDOT Number */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-5538">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-5539">
              Who Needs a USDOT Number?
            </h2>

            <div className="space-y-4" data-id="element-5540">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6" data-id="element-5541">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-5542">
                  ✅ Required For:
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-5543">
                  <li className="flex items-start" data-id="element-5544">
                    <CheckCircle className="text-blue-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-5545" />
                    <span data-id="element-5546">
                      Companies operating commercial vehicles in interstate
                      commerce (passengers or cargo)
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-5547">
                    <CheckCircle className="text-blue-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-5548" />
                    <span data-id="element-5549">
                      Intrastate hazardous materials carriers requiring a safety
                      permit
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-5550">
                    <CheckCircle className="text-blue-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-5551" />
                    <span data-id="element-5552">
                      All commercial motor vehicle registrants in the 24
                      required states (even intrastate)
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-6" data-id="element-5553">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-5554">
                  ❌ Generally NOT Required For:
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-5555">
                  <li className="flex items-start" data-id="element-5556">
                    <span className="mr-2" data-id="element-5557">•</span>
                    <span data-id="element-5558">Companies operating exclusively as brokers</span>
                  </li>
                  <li className="flex items-start" data-id="element-5559">
                    <span className="mr-2" data-id="element-5560">•</span>
                    <span data-id="element-5561">Non-vehicle-operating shippers</span>
                  </li>
                  <li className="flex items-start" data-id="element-5562">
                    <span className="mr-2" data-id="element-5563">•</span>
                    <span data-id="element-5564">Freight forwarders (without vehicles)</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Key Points Summary */}
          <div className="bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg shadow-xl p-8 mb-8" data-id="element-5565">
            <h2 className="text-3xl font-bold mb-6" data-id="element-5566">Key Points to Remember</h2>

            <div className="space-y-3" data-id="element-5567">
              <div className="flex items-start" data-id="element-5568">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-5569" />
                <span data-id="element-5570">
                  USDOT Number is a unique identifier for safety monitoring
                </span>
              </div>
              <div className="flex items-start" data-id="element-5571">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-5572" />
                <span data-id="element-5573">
                  Required for interstate commerce and certain intrastate
                  operations
                </span>
              </div>
              <div className="flex items-start" data-id="element-5574">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-5575" />
                <span data-id="element-5576">
                  New entrants automatically enrolled in 18-month safety
                  assurance program
                </span>
              </div>
              <div className="flex items-start" data-id="element-5577">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-5578" />
                <span data-id="element-5579">
                  24 states require USDOT Numbers for all commercial vehicle
                  registrations
                </span>
              </div>
              <div className="flex items-start" data-id="element-5580">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-5581" />
                <span data-id="element-5582">Brokers and non-vehicle operators generally exempt</span>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg shadow-xl p-8 text-center" data-id="element-5583">
            <h2 className="text-3xl font-bold mb-4" data-id="element-5584">
              Need Help with DOT Compliance?
            </h2>
            <p className="text-xl mb-6" data-id="element-5585">
              Expert guidance on USDOT Number requirements and registration
            </p>
            <a href="tel:2484788382" className="inline-flex items-center bg-[#B1050B] hover:bg-[#8A0408] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-5586">
              <Phone className="mr-3" size={24} data-id="element-5587" />
              Call (248) 478-8382
            </a>
            <p className="mt-4 text-gray-300" data-id="element-5588">
              24175 Industrial Park Dr., Farmington Hills, MI 48335
            </p>
          </div>
        </div>
      </div>
    </div>;
}